import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useFlash from "go-alert/AlertMessage";
import Header, { ButtonProps } from "go-app/components/Header";
import { MobileActionProps } from "go-app/components/MobileActions/MobileAction";
import MobileActions from "go-app/components/MobileActions/MobileActions";
import handleError from "go-app/services/errors";
import { LoadingContainer } from "go-core/components/Loading";
import { useWindowSize } from "go-core/components/useWindowSize";
import { CustomFieldTemplateApi } from "go-segment/services/types";
import FormatResourceStatus from "../../../../../../../../../components/Common/Formatters/FormatResourceStatus/FormatResourceStatus";
import { partnerApi } from "../../../../../../../../../services/Api/Partner/partnerApi";
import { PartnerApi, PartnerDashboardApi } from "../../../../../../../../../services/Api/Partner/partnerTypes";
import { api } from "../../../../../../../../../services/Api/api";
import { OrganizationsDashboardApi } from "../../../../../../../../../services/Api/types";
import ChurnCard from "../../../../../../../components/Admin/Dashboard/ChurnCard";
import { useUserPermissions } from "../../../../../hooks";
import PartnerAffiliateMessageCard from "./PartnerAffiliateMessageCard";
import PartnerBalanceCard from "./PartnerBalanceCard";
import PartnerCodesCard from "./PartnerCodesCard";
import PartnerCompanyCard from "./PartnerCompanyCard";
import PartnerContactCard from "./PartnerContactCard";
import PartnerCustomFieldsCard from "./PartnerCustomFieldsCard";
import PartnerInfoCard from "./PartnerInfoCard";
import PartnerLoansCard from "./PartnerLoansCard";
import PartnerProvisionsCard from "./PartnerProvisionsCard";
import PartnerResellerMessageCard from "./PartnerResellerMessageCard";
import PartnerResselerServiceContactCard from "./PartnerResselerServiceContactCard";

interface Props {
	partner: PartnerApi;
}

const PartnerPreview = ({ partner }: Props) => {
	const { addFlash } = useFlash();
	const { t } = useTranslation("admin");
	const [dashboard, setDashboard] = useState<PartnerDashboardApi | undefined>(undefined);
	const [organizationsDashboard, setOrganizationsDashboard] = useState<OrganizationsDashboardApi | undefined>(
		undefined
	);
	const { hasMoneyFlowPermission } = useUserPermissions(partner?.current_user_permissions);
	const [customFieldsConfig, setCustomFieldsConfig] = useState<CustomFieldTemplateApi[] | undefined>();
	const isMobile = useWindowSize().isMobile;

	useEffect(() => {
		handleInit();
	}, []);

	const handleInit = async () => {
		await fetchPartnerDashboard();
		await fetchOrganizationsDashboard();
		await fetchResourceConfig();
	};

	const fetchPartnerDashboard = async () => {
		try {
			const res = await partnerApi.getPartnerDashboard(Number(partner?.id));
			setDashboard(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const fetchOrganizationsDashboard = async () => {
		try {
			const res = await partnerApi.getOrganizationsDashboard(Number(partner.id));
			setOrganizationsDashboard(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const fetchResourceConfig = async () => {
		try {
			const params = {
				resource_name: "PARTNER_ORGANIZATION",
				custom_field_resource_type: "ORGANIZATION",
				custom_field_permission_scope: "WRITE",
			};
			const res = await api.getResourceConfig(params);
			setCustomFieldsConfig(res.custom_fields);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	if (!dashboard || !organizationsDashboard || !customFieldsConfig) return <LoadingContainer />;

	const renderTitle = () => (
		<>
			{t("common.word.partner")} {partner.name}
			<FormatResourceStatus status={partner.status} />
			{partner.parent && <span className="badge bg-primary text-wrap">{partner.parent.name}</span>}
		</>
	);

	const buttons: ButtonProps[] = [
		{
			title: t("common.action.edit", { ns: "lib" }),
			path: `/partnership/${partner.id}/edit`,
			variant: "light",
		},
	];
	const mobileActions: MobileActionProps[] = [
		{
			title: t("common.action.edit", { ns: "lib" }),
			path: `/partnership/${partner.id}/edit`,
		},
	];

	return (
		<div className={"content-preview"}>
			<MobileActions actions={mobileActions} />
			<Header title={renderTitle()} buttons={isMobile ? [] : buttons} />
			<div className={"content-preview-body"}>
				<div className={"row"}>
					<div className={"col-md-4"}>
						<ChurnCard
							isAdminMode={false}
							dashboard={organizationsDashboard}
							partnerId={partner?.id}
							type="DEFAULT"
						/>
						<ChurnCard
							isAdminMode={false}
							dashboard={organizationsDashboard}
							partnerId={partner?.id}
							type="FUTURE"
						/>
						{hasMoneyFlowPermission && <PartnerBalanceCard balance={dashboard?.balance} />}
						<PartnerLoansCard
							count_of_loans_to_close={Number(dashboard?.count_of_loans_to_close)}
							count_of_closed_loans={Number(dashboard?.count_of_closed_loans)}
						/>
						<PartnerCompanyCard partner={partner} />
						<PartnerInfoCard partner={partner} />
						{(partner.contact_person || partner.company_email || partner.company_phone_number) && (
							<PartnerContactCard partner={partner} />
						)}
						{partner.codes.length > 0 && <PartnerCodesCard codes={partner.codes} />}
						{partner.custom_fields.length > 0 && (
							<PartnerCustomFieldsCard
								customFields={partner.custom_fields}
								customFieldsConfig={customFieldsConfig}
							/>
						)}
					</div>
					<div className={"col-md-8"}>
						{partner.type === "AFFILIATE" && <PartnerAffiliateMessageCard />}
						{partner.type === "RESELLER" && <PartnerResellerMessageCard />}
						{partner.type === "RESELLER" && <PartnerResselerServiceContactCard />}
						<PartnerProvisionsCard provisions={partner.provisions} />
					</div>
				</div>
			</div>
		</div>
	);
};
export default PartnerPreview;
